/**
 * 브랜드 초대 받는 쪽 — `/invite?t=` (261013-account-brand-membership). 공개 화면이다.
 * 그 주소의 계정이 없으면 비밀번호를 정해 만들고, **있으면 기존 비밀번호로** 소속만 더한다(같은 사람이 비밀번호 둘을 갖지 않게).
 *
 * ★ 토큰은 주소창에서 곧바로 지운다(방문 기록·Referer). 무효 토큰은 한 문장으로 답한다 — 서버도 사유를 가르지 않는다
 */
import { SHELL, WHITE_LABEL } from '../lib/branding.ts';
import { brand } from '@geo-admin/shared/contract/api/index.ts';
import { Password } from '@geo-admin/shared/contract/api/auth.ts';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AuthCard, AuthShell, authField, HeroPanel, StatusCircle } from '../components/AuthShell.tsx';
import { CheckIcon } from '../components/icons.tsx';
import { SupportContact } from '../components/SupportContact.tsx';
import { Button } from '../components/ui/button.tsx';
import { FieldError, Input, Label } from '../components/ui/form.tsx';
import { Loading } from '../components/ui/states.tsx';
import { call } from '../lib/call.ts';
import { useUrlToken } from '../lib/urlToken.ts';

type Inspect = Awaited<ReturnType<typeof call<typeof brand.inspectInvite>>>;

const ROLE_WORD = { owner: '소유자', manager: '관리자' } as const;

/** 초대 내용 — 토큰이 무효면 실패 문구 하나 */
function useInspect(token: string) {
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'ok'; data: Inspect } | { kind: 'fail'; message: string }
  >({ kind: 'loading' });
  useEffect(() => {
    if (!token) {
      setState({ kind: 'fail', message: '초대 주소가 올바르지 않습니다. 받은 메일·메시지의 주소를 다시 열어 주세요.' });
      return;
    }
    void call(brand.inspectInvite, { body: { token } })
      .then((data) => setState({ kind: 'ok', data }))
      .catch((e: Error) => setState({ kind: 'fail', message: e.message }));
  }, [token]);
  return state;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <AuthShell aside={<HeroPanel eyebrow="초대" title={`${SHELL.serviceName} 에 초대받으셨습니다`} />}>
      {children}
      <SupportContact />
    </AuthShell>
  );
}

function Done({ title, description, to, label }: { title: string; description: string; to: string; label: string }) {
  const navigate = useNavigate();
  return (
    <Shell>
      <AuthCard
        icon={
          <StatusCircle tone="ok">
            <CheckIcon className="h-7 w-7" strokeWidth={3} />
          </StatusCircle>
        }
        title={title}
        description={description}
      >
        <Button variant="live" size="lg" className="w-full" onClick={() => void navigate({ to })}>
          {label}
        </Button>
      </AuthCard>
    </Shell>
  );
}

function Consents({
  value,
  onChange,
}: {
  value: { terms: boolean; privacy: boolean; marketing: boolean };
  onChange: (v: { terms: boolean; privacy: boolean; marketing: boolean }) => void;
}) {
  // 화이트라벨이면 대행사 문서로 안내하고 우리 마케팅 동의는 받지 않는다(서버도 기록하지 않는다 — 261025-agency-white-label)
  const external = (url: string | null, label: string) =>
    url ? (
      <a href={url} target="_blank" rel="noreferrer" className="underline">
        {label}
      </a>
    ) : (
      label
    );
  const item = (key: 'terms' | 'privacy' | 'marketing', label: string, to: '/terms' | '/privacy' | '/marketing') => (
    <label className="flex items-center gap-2">
      <input type="checkbox" checked={value[key]} onChange={(e) => onChange({ ...value, [key]: e.target.checked })} />
      <span>
        {key === 'marketing' ? '(선택)' : '(필수)'}{' '}
        {WHITE_LABEL ? (
          external(key === 'terms' ? SHELL.termsUrl : SHELL.privacyUrl, label)
        ) : (
          <Link to={to} target="_blank" className="underline">
            {label}
          </Link>
        )}
        에 동의합니다
      </span>
    </label>
  );
  return (
    <div className="grid gap-2 text-sm">
      {item('terms', '이용약관', '/terms')}
      {item('privacy', '개인정보 수집·이용', '/privacy')}
      {WHITE_LABEL ? null : item('marketing', '마케팅 정보 수신', '/marketing')}
    </div>
  );
}

// ── 브랜드 초대 ──────────────────────────────────────────────────────────

export function InviteAcceptPage() {
  const token = useUrlToken();
  const inspect = useInspect(token);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [consents, setConsents] = useState({ terms: false, privacy: false, marketing: false });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ existing: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  if (inspect.kind === 'loading') return <Loading />;
  if (inspect.kind === 'fail')
    return (
      <Shell>
        <AuthCard title="초대를 열 수 없습니다" description={inspect.message}>
          <Button variant="outline" size="lg" className="w-full" onClick={() => void navigate({ to: '/login' })}>
            로그인 화면으로
          </Button>
        </AuthCard>
      </Shell>
    );

  const inv = inspect.data;
  if (done)
    return (
      <Done
        title={done.existing ? '연결됐습니다' : '계정이 만들어졌습니다'}
        description={
          done.existing
            ? `쓰시던 이메일·비밀번호로 로그인하면 「${inv.brandName}」도 함께 보입니다.`
            : '방금 정한 비밀번호로 로그인해 주세요.'
        }
        to="/login"
        label="로그인하러 가기"
      />
    );

  const existing = inv.accountExists;
  const pwError = !existing && password && !Password.safeParse(password).success ? '비밀번호는 8자 이상' : null;
  const confirmError = !existing && confirm && confirm !== password ? '두 입력이 다릅니다' : null;
  const ready = existing
    ? password.length > 0
    : name.trim() && password && !pwError && confirm === password && consents.terms && consents.privacy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await call(brand.acceptInvite, {
        body: existing
          ? { existing: true, token, password }
          : {
              existing: false,
              token,
              password,
              name: name.trim(),
              agreeTerms: true,
              agreePrivacy: true,
              agreeMarketing: consents.marketing,
            },
      });
      setDone({ existing });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const who = inv.inviterName ? `${inv.inviterName}님이 ` : '';
  const title = `「${inv.brandName}」 ${ROLE_WORD[inv.role]} 초대`;
  const description = existing
    ? `${who}${inv.maskedEmail} 로 초대했습니다. 이미 쓰시는 계정이 있습니다 — 그 비밀번호를 넣으면 같은 로그인으로 이 브랜드도 관리합니다.`
    : `${who}${inv.maskedEmail} 로 초대했습니다. 비밀번호를 정하면 이 브랜드의 보고서와 기사를 함께 관리합니다.`;

  return (
    <Shell>
      <AuthCard title={title} description={description}>
        <form className="grid gap-4" onSubmit={(e) => void submit(e)}>
          {existing ? (
            <Label>
              쓰시던 비밀번호
              <Input
                type="password"
                autoComplete="current-password"
                className={authField}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Label>
          ) : (
            <>
              <Label>
                이름
                <Input
                  className={authField}
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Label>
              <div className="grid items-start gap-4 sm:grid-cols-2">
                <Label>
                  비밀번호
                  <Input
                    type="password"
                    autoComplete="new-password"
                    className={authField}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <FieldError>{pwError}</FieldError>
                </Label>
                <Label>
                  비밀번호 확인
                  <Input
                    type="password"
                    autoComplete="new-password"
                    className={authField}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                  <FieldError>{confirmError}</FieldError>
                </Label>
              </div>
              <Consents value={consents} onChange={setConsents} />
            </>
          )}
          <FieldError>{error}</FieldError>
          <Button type="submit" variant="live" size="lg" className="w-full" disabled={!ready || busy}>
            {existing ? '연결하기' : '계정 만들기'}
          </Button>
        </form>
      </AuthCard>
    </Shell>
  );
}
